import { refreshCurrentLegacyFrame, useCurrentUser, useRouteAuthorisation } from '@economic/react-app';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ShowTransactionsView from '~/app/pages/accounting/lists/FindEntries/ShowTransactionsView';
import CustomerPaymentsOnboardingDialog from '~/market/onboarding/customer-payments-feature';
import RackbeatLagerOnboardingDialog from '~/market/onboarding/reackbeat-lager-feature';
import {
    RackbeatLagerOffboardingDialogNative,
    RackbeatLagerOnboardingDialogNative,
} from '~/market/onboarding/reackbeat-lager-native-feature';
import PaymentCardsOnboardingDialog from 'libs/payment-cards-feature/src/common/OnboardingDialog';
import ChangePlanDialog from '../pages/accounting/bank/smartpay/onboarding/components/ChangePlanModal/ChangePlanModal';
import SignupDialogProvider from '../pages/accounting/bank/smartpay/onboarding/states/Unregistered/SignupDialogProvider';
import SysPageModal from '../pages/accounting/bank/smartpay/SysPageModal/SysPageModal';
import { ConnectToSkatVatDialog } from '../pages/reports/vat/statement/skat/ConnectToSkatVatDialog';
import { SkatVatStatementReceiptDialog } from '../pages/reports/vat/statement/skat/SkatVatStatementReceiptDialog';
import { TransferToSkatDialog } from '../pages/reports/vat/statement/skat/TransferToSkatDialog';
import SendBulkCustomerLedgerCardsDialogContainer from '../pages/sales/customers/ledger-cards/send-bulk/SendBulkCustomerLedgerCardsDialogContainer';
import SendSingleCustomerLedgerCardDialogContainer from '../pages/sales/customers/ledger-cards/send-single/SendSingleCustomerLedgerCardDialogContainer';
import CopyInvoice from '../pages/sales/invoicing/archive/copy-invoice/CopyInvoice';
import SendInvoiceDialogContainer from '../pages/sales/invoicing/archive/send-invoice/SendInvoiceDialogContainer';
import SendInvoicesDialogContainer from '../pages/sales/invoicing/archive/send-invoices/SendInvoicesDialogContainer';
import SendReminderEmailFromArchiveDialogContainer from '../pages/sales/invoicing/archive/send-reminder/SendReminderEmailFromArchiveDialogContainer';
import CopyAccrualsDialog from '../pages/sales/invoicing/CopyAccrualsDialog';
import { LegacyBulkBookDraftInvoicesDialogContainer } from '../pages/sales/invoicing/invoices/book-and-send/book-and-send-bulk/BulkBookDraftInvoicesDialogContainer';
import BookDraftInvoiceDialogContainer from '../pages/sales/invoicing/invoices/book-and-send/book-and-send-single/BookDraftInvoiceDialogContainer';
import { legacyForms } from '../pages/sales/invoicing/invoices/utils';
import SendOrderDialogContainer from '../pages/sales/invoicing/orders/send-order/SendOrderDialogContainer';
import SendDraftQuoteDialogContainer from '../pages/sales/invoicing/quotes/send-quote/SendDraftQuoteDialogContainer';
import BulkBookAndSendReminderDialogContainer from '../pages/sales/invoicing/reminders/book-and-send-bulk/BulkBookAndSendReminderDialogContainer';
import BookAndSendSingleReminderDialogContainer from '../pages/sales/invoicing/reminders/book-and-send-single/BookAndSendSingleReminderDialogContainer';
import ResendInvoiceDialogContainer from '../pages/sales/invoicing/sent/resend-invoice/ResendInvoiceDialogContainer';
import { ResendMultipleInvoicesDialogContainer } from '../pages/sales/invoicing/sent/resend-multiple-invoices/ResendMultipleInvoicesDialogContainer';
import { ResendOrderDialogContainer } from '../pages/sales/invoicing/sent/resend-order/ResendOrderDialogContainer';
import { ResendQuoteDialogContainer } from '../pages/sales/invoicing/sent/resend-quote/ResendQuoteDialogContainer';
import ResendReminderDialogContainer from '../pages/sales/invoicing/sent/resend-reminder/ResendReminderDialogContainer';
import { ResendStatementOfAccount } from '../pages/sales/invoicing/sent/resend-statement-of-account/ResendStatementOfAccount';
import AddProductCollectionDialog from '../pages/sales/invoicing/shared/components/AddProductCollectionDialog';
import { BulkDimensionDialog } from '../pages/sales/invoicing/shared/components/BulkDimensionDialog';
import { BulkDiscountDialog } from '../pages/sales/invoicing/shared/components/BulkDiscountDialog';
import CopyDistributionKeyDialog from '../pages/settings/categories-and-units/departments/CopyDistributionKeyDialog';
import CreateDistributionKeyDialog from '../pages/settings/categories-and-units/departments/CreateDistributionKeyDialog';
import EditDistributionKeyDialog from '../pages/settings/categories-and-units/departments/EditDistributionKeyDialog';
import { ManualSynchronisationDialog } from '../pages/settings/integrations/smartbank/components/ManualSynchronisationDialog';
import CancelAministratorDeregistrationDialog from '../pages/system/administrators/CancelAdministratorDeregistrationDialog';
import DeregisterAdministratorDialog from '../pages/system/administrators/DeregisterAdministratorDialog';
import ExternalBillingItemsDialog from '../pages/system/externalBillingItems/ExternalBillingItemsDialog';
import CreatePartialInvoiceDialog from '../views/SalesDocuments/Lists/Orders/PartialInvoicing/CreatePartialInvoiceDialog';
import TransferToInvoicingDialog from '../views/SalesDocuments/Lists/Orders/TransferToInvoicing/TransferToInvoicingDialog';
import HashedRoute from './components/HashedRoute';
import ChartOfAccountsDialog from './components/modals/ChartOfAccountsDialog';
import CompanyDeregisterDialog from './components/modals/CompanyDeregisterDialog';

export default function LegacyDialogRoutes() {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useCurrentUser();
    const [isProjectManager] = useRouteAuthorisation('/projects/responsibility/invoices');

    const prefix = user.isBasicUser ? '/basic' : '';

    function removeHashFragmentAndRefresh() {
        navigate(location.pathname);
        refreshCurrentLegacyFrame('content');
    }

    function navigateAfterBooking(invoiceId: number): void {
        const urlParams = new URLSearchParams(location.search);
        const returnTo = urlParams.get('returnTo');

        switch (returnTo) {
            case legacyForms.quotations:
                navigate(`${prefix}/sales/invoicing/quotations`);
                break;

            case legacyForms.orders:
                navigate(`${prefix}/sales/invoicing/orders`);
                break;

            case legacyForms.archive:
                navigate(`${prefix}/sales/invoicing/archive/invoices?${invoiceId ? `fakturanr=${invoiceId}` : ''}`);
                break;

            case legacyForms.customers:
                navigate(`${prefix}/sales/customers`);
                break;

            case legacyForms.customerProjects:
                navigate(`${prefix}/sales/customers/projects`);
                break;

            default:
                if (isProjectManager) {
                    navigate(`/projects/responsibility/invoices`);
                } else {
                    navigate(`${prefix}/sales/invoicing/invoices`);
                }
                break;
        }
    }

    return (
        <>
            <HashedRoute hash="#book-draft">
                <BookDraftInvoiceDialogContainer onBooked={navigateAfterBooking} />
            </HashedRoute>

            <HashedRoute hash="#book-bulk">
                <LegacyBulkBookDraftInvoicesDialogContainer onBooked={removeHashFragmentAndRefresh} />
            </HashedRoute>

            <HashedRoute hash="#book-reminder">
                <BookAndSendSingleReminderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#book-bulk-reminder">
                <BulkBookAndSendReminderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#bulk-discount">
                <BulkDiscountDialog onSubmitted={() => refreshCurrentLegacyFrame('content')} />
            </HashedRoute>

            <HashedRoute hash="#bulk-dimension">
                <BulkDimensionDialog onSubmitted={() => refreshCurrentLegacyFrame('content')} />
            </HashedRoute>

            <HashedRoute hash="#resend-invoice">
                <ResendInvoiceDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-multipleinvoices">
                <ResendMultipleInvoicesDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#book-quote">
                <SendDraftQuoteDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-quote">
                <ResendQuoteDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#book-order">
                <SendOrderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-order">
                <ResendOrderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-reminder">
                <ResendReminderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-statementofaccount">
                <ResendStatementOfAccount />
            </HashedRoute>

            <HashedRoute hash="#copy-invoice">
                <CopyInvoice />
            </HashedRoute>

            <HashedRoute hash="#send-invoice">
                <SendInvoiceDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#send-invoices">
                <SendInvoicesDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#send-reminder-email-from-archive">
                <SendReminderEmailFromArchiveDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#send-single-ledger-card">
                <SendSingleCustomerLedgerCardDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#send-bulk-ledger-cards">
                <SendBulkCustomerLedgerCardsDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#onboardRackbeatLager">
                <RackbeatLagerOnboardingDialog />
            </HashedRoute>

            <HashedRoute hash="#onboardRackbeatLagerNative">
                <RackbeatLagerOnboardingDialogNative />
            </HashedRoute>

            <HashedRoute hash="#offboardRackbeatLagerNative">
                <RackbeatLagerOffboardingDialogNative />
            </HashedRoute>

            <HashedRoute hash="#onboardSmartPay">
                <SignupDialogProvider inMarket defaultOpen />
            </HashedRoute>

            <HashedRoute hash="#onboardPaymentCards">
                <PaymentCardsOnboardingDialog inMarket defaultOpen />
            </HashedRoute>

            <HashedRoute hash="#changeSmartPayPlan">
                <ChangePlanDialog />
            </HashedRoute>

            <HashedRoute hash="#SmartPayIntegrationDetails">
                <SysPageModal />
            </HashedRoute>

            <HashedRoute hash="#ExternalBillingItemsModal">
                <ExternalBillingItemsDialog />
            </HashedRoute>

            <HashedRoute hash="#deregister-company">
                <CompanyDeregisterDialog />
            </HashedRoute>

            <HashedRoute hash="#create-distribution-key">
                <CreateDistributionKeyDialog />
            </HashedRoute>

            <HashedRoute hash="#edit-distribution-key">
                <EditDistributionKeyDialog />
            </HashedRoute>

            <HashedRoute hash="#copy-distribution-key">
                <CopyDistributionKeyDialog />
            </HashedRoute>

            <HashedRoute hash="#copy-accruals">
                <CopyAccrualsDialog />
            </HashedRoute>

            <HashedRoute hash="#deregister-administrator">
                <DeregisterAdministratorDialog />
            </HashedRoute>

            <HashedRoute hash="#cancel-administrator-deregistration">
                <CancelAministratorDeregistrationDialog />
            </HashedRoute>

            <HashedRoute hash="#smartbank-manual-synchronisation-enabled">
                <ManualSynchronisationDialog
                    isManualSyncAllowed
                    hasMoreAiiaIntegrations
                    refreshSettings={() => refreshCurrentLegacyFrame('content')}
                    defaultOpen
                />
            </HashedRoute>

            <HashedRoute hash="#smartbank-manual-synchronisation-disabled">
                <ManualSynchronisationDialog
                    isManualSyncAllowed={false}
                    hasMoreAiiaIntegrations
                    refreshSettings={() => refreshCurrentLegacyFrame('content')}
                    defaultOpen
                />
            </HashedRoute>

            <HashedRoute hash="#show-transactions">
                <ShowTransactionsView />
            </HashedRoute>

            <HashedRoute hash="#add-product-collection">
                <AddProductCollectionDialog />
            </HashedRoute>

            <HashedRoute hash="#connect-to-skat-vat">
                <ConnectToSkatVatDialog />
            </HashedRoute>

            <HashedRoute hash="#transfer-to-skat">
                <TransferToSkatDialog />
            </HashedRoute>

            <HashedRoute hash="#view-skat-vat-receipt">
                <SkatVatStatementReceiptDialog />
            </HashedRoute>

            <HashedRoute hash="#create-partial-invoice">
                <CreatePartialInvoiceDialog />
            </HashedRoute>

            <HashedRoute hash="#transfer-to-invoicing">
                <TransferToInvoicingDialog open />
            </HashedRoute>

            <HashedRoute hash="#onboardPaymentLink">
                <CustomerPaymentsOnboardingDialog />
            </HashedRoute>

            <HashedRoute hash="#chart-of-accounts-search-dialog">
                <ChartOfAccountsDialog />
            </HashedRoute>
        </>
    );
}
